import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Lesson } from '../types/lesson';
import type { Order } from '../types/order';
import LessonDeliveredDetail from './LessonDeliveredDetail';
import BackButton from './BackButton';
import { canUploadInProgram } from '../utils/programSlots';
import { useIsMobile } from '../contexts/ResponsiveContext';
import UploadNextLessonModal from './UploadNextLessonModal';
import { useUserPendingUploads } from '../hooks/usePendingUploads';
import { useUserIdentity } from '../hooks/useUserIdentity';
import { formatLessonDate } from '../utils/lessonDate';

interface BundleLessonDetailProps {
  order: Order;
  lesson: Lesson;
  /** All lessons in this order — used to determine if this is the most recent delivered lesson */
  allOrderLessons: Lesson[];
  onBack?: () => void;
  onMessage?: () => void;
  /** 'page' (default) is the full-screen route treatment: a 760px centred
   *  column on the grey app background, with its own back button. 'panel'
   *  fills a two-pane container instead — full width, white, no back button,
   *  since the list beside it is already the way back. Padding is identical
   *  in both. */
  variant?: 'page' | 'panel';
}

/**
 * Program lesson screen.
 *
 * A DELIVERED program lesson is the single-lesson delivered screen
 * (LessonDeliveredDetail) hosted with a `program` prop: title "Lesson N of M",
 * the same coach row, sections and player, the review presented as the prompt
 * card but gated to the program's rules (one per order, lesson 2+, most
 * recent delivered, button id leave-review-btn), and no upsell. The program's
 * own affordances — next-lesson upload and the final-lesson progress-report
 * banner — are appended below the body via `footerExtras`; their handlers and
 * the upload modal stay here, so the upload path is untouched.
 *
 * The NON-delivered branch (reserved / in-progress) keeps its own layout
 * below: LessonDeliveredDetail is delivered-only and BundleProgress routes
 * active lessons to the waiting screen, so this branch is defensive.
 */
export default function BundleLessonDetail({
  order,
  lesson,
  allOrderLessons,
  onBack,
  variant = 'page',
}: BundleLessonDetailProps) {
  const isPanel = variant === 'panel';
  const navigate = useNavigate();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const isMobile = useIsMobile();
  const { uploads: pendingUploads, hasBlockingUpload } = useUserPendingUploads();
  const activePendingUpload = pendingUploads.find(
    u => u.existingLessonId === lesson.id && (u.status === 'uploading' || u.status === 'uploaded' || u.status === 'processing')
  );

  const lessonNum = lesson.lessonNumber ?? 1;
  const isMostRecent = lessonNum === Math.max(
    ...allOrderLessons.filter(l => l.status === 'delivered').map(l => l.lessonNumber ?? 0),
    0
  );

  // Live identity overrides the order's denormalized coachName/Avatar
  // snapshot — see useUserIdentity docstring. Non-delivered branch only;
  // the delivered body resolves its own.
  const liveCoach = useUserIdentity(order.coachId, 'coach');
  const coachDisplayName = liveCoach?.name ?? order.coachName ?? '';
  const coachAvatarUrl = liveCoach?.avatarUrl ?? order.coachAvatarUrl;
  const totalLessons = order.totalLessons ?? 4;
  const coachFirstName = coachDisplayName.split(' ')[0] || 'your coach';
  const coachInitials = (coachDisplayName || 'C')
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const isFinalLesson = lessonNum === totalLessons;
  const isReserved = lesson.status === 'reserved';
  const isDelivered = lesson.status === 'delivered';

  // Show upload CTA if: reserved lesson OR (most recent delivered, not final, no in_progress, not refunded)
  // Suppress in both cases when any upload is already active (uploading/processing).
  // The rule itself moved to utils/programSlots so the program list and this
  // screen cannot disagree about whose turn it is. The expression is unchanged.
  const isRefunded = order.refundRequested === true || order.paymentStatus === 'refunded';
  const inProgressExists = allOrderLessons.some(l => l.status === 'in_progress');
  const showUploadCta = canUploadInProgram({
    hasBlockingUpload,
    isReserved,
    hasActivePendingUploadForThisLesson: !!activePendingUpload,
    isFinalLesson,
    inProgressExists,
    isMostRecent,
    isRefunded,
  });
  // For reserved lesson, upload video for THIS lesson; otherwise upload for the next
  const uploadLessonNum = isReserved ? lessonNum : lessonNum + 1;
  const nextLessonNum = lessonNum + 1;

  const deliveredDate = lesson.deliveredAt
    ? formatLessonDate(lesson.deliveredAt)
    : '';

  /* Progress Report banner — final lesson only. Shared by both branches. */
  const progressReportBanner = isFinalLesson ? (
    <div className={`bg-biscayne rounded-xl px-6 py-5 mb-4 ${isMobile ? '' : 'flex items-center justify-between gap-4'}`}>
      <div className={isMobile ? 'mb-3' : ''}>
        <div className="text-[11px] font-bold uppercase tracking-[.07em] text-white/80 mb-1">
          Training program · All {totalLessons} lessons delivered
        </div>
        <div className="text-[16px] font-extrabold text-white mb-1">Your progress report is ready.</div>
        <div className="text-[12px] text-white/55 leading-[1.5]">
          {coachFirstName} wrote a full summary of your progress — what improved, what to focus on next, and a personal note.
        </div>
      </div>
      <button
        className={`text-white text-[13px] font-bold px-4 py-2.5 rounded-xl whitespace-nowrap transition-colors flex-shrink-0 bg-biscayne ${isMobile ? 'w-full text-center border border-white/30' : ''}`}
        onClick={() => navigate(`/progress-report/${order.id}`)}
      >
        View Report →
      </button>
    </div>
  ) : null;

  /* Upload-Next CTA — the natural conversion path on a program lesson (no
     upsell to a new program; the player is already in one). Inline (not
     sticky on mobile). Shared by both branches; delivered gets extra top
     margin to clear the body's last section. */
  const uploadCta = (topMargin: string) => showUploadCta ? (
    <div className={`${topMargin} mb-3`}>
      <button
        onClick={() => setShowUploadModal(true)}
        className="w-full px-4 py-3 rounded-xl text-[14px] font-bold text-white bg-biscayne hover:bg-db-navy-hover transition-colors text-center btn-press"
      >
        {isReserved ? 'Upload Your Video' : `Upload Video for Lesson ${nextLessonNum}`}
      </button>
    </div>
  ) : null;

  const uploadModal = showUploadModal ? (
    <UploadNextLessonModal
      order={order}
      nextLessonNum={uploadLessonNum}
      existingLessonId={isReserved ? lesson.id : undefined}
      onClose={() => setShowUploadModal(false)}
    />
  ) : null;

  // ── DELIVERED: the single-lesson screen, program-hosted ───────────────────
  if (isDelivered) {
    return (
      <>
        <LessonDeliveredDetail
          order={order}
          lesson={lesson}
          onBack={onBack}
          variant={variant}
          program={{
            lessonNumber: lessonNum,
            totalLessons,
            reviewEligible: lessonNum >= 2 && isMostRecent,
          }}
          footerExtras={
            <>
              {progressReportBanner}
              {uploadCta('mt-8')}
            </>
          }
        />
        {uploadModal}
      </>
    );
  }

  // ── NON-DELIVERED: reserved / in-progress states, unchanged ───────────────
  return (
    <div className={`flex flex-col h-full overflow-y-auto${isPanel ? '' : ' bg-db-bg'}`}>
      <div className={`p-6 md:p-8 pt-5 md:pt-8 w-full ${isPanel ? '' : 'max-w-[760px] mx-auto'} ${isMobile ? 'pb-[calc(32px+env(safe-area-inset-bottom,0px))]' : ''}`}>

        {!isPanel && <BackButton onClick={onBack} fallback="/lessons" sticky />}

        {/* Banner — reserved / in-progress states. */}
        <div className="rounded-xl mb-6 bg-biscayne" style={{ padding: '22px 26px' }}>
          <div className="text-[15px] font-extrabold text-white mb-0.5">
            {isReserved
              ? (activePendingUpload
                ? `Video uploading to ${coachFirstName}`
                : `Your spot is held with ${coachFirstName}`)
              : `${coachFirstName} delivered Lesson ${lessonNum}`}
          </div>
          <div className="text-[13px]" style={{ color: 'rgba(255,255,255,.55)' }}>
            Training program · Lesson {lessonNum} of {totalLessons}
            {!isReserved && deliveredDate ? ` · Delivered ${deliveredDate}` : ''}
            {isReserved
              ? (activePendingUpload
                ? (activePendingUpload.status === 'uploading'
                  ? ` · Uploading ${activePendingUpload.uploadProgress ?? 0}%`
                  : ' · Processing your video...')
                : ' · Submit your video any time to start')
              : ''}
          </div>
        </div>

        {/* Main card — coach row + small placeholder. */}
        <div className="bg-white rounded-xl shadow-sm mb-4" style={{ padding: '22px 24px' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 aspect-[4/5] rounded-[8px] bg-biscayne flex items-center justify-center overflow-hidden flex-shrink-0">
                {coachAvatarUrl ? (
                  <img src={coachAvatarUrl} alt={coachDisplayName} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[13px] font-bold text-white">{coachInitials}</span>
                )}
              </div>
              <div>
                <div className="text-[14px] font-bold text-db-text-1">{coachDisplayName}</div>
                <div className="text-[12px] text-db-text-1">
                  Training program · Lesson {lessonNum} of {totalLessons}
                </div>
              </div>
            </div>
          </div>

          {lesson.playerVideoUrl && (
            <div className="border-t border-db-border pt-3.5">
              <div className="text-[12px] font-semibold text-db-text-3 mb-2">Your video</div>
              <div
                className="flex items-center gap-3 p-3 rounded-lg cursor-pointer bg-db-bg"
                onClick={() => window.open(lesson.playerVideoUrl, '_blank')}
              >
                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-biscayne">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><polygon points="9.5,7.5 16.5,12 9.5,16.5" /></svg>
                </div>
                <div>
                  <div className="text-[13px] font-semibold text-db-text-1">Your video</div>
                  <div className="text-[12px] text-db-text-3 mt-0.5">Your submitted video</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Coach notes */}
        {lesson.coachNotes && (
          <div className="bg-white rounded-xl shadow-sm mb-4" style={{ padding: '18px 20px' }}>
            <div className="text-[12px] font-semibold text-db-text-3 mb-2">
              {coachFirstName}'s notes
            </div>
            <p className="text-[14px] text-db-text-1 leading-relaxed whitespace-pre-wrap">{lesson.coachNotes}</p>
          </div>
        )}

        {progressReportBanner}

        {uploadCta('mt-0')}

        {isReserved && activePendingUpload && (
          <div className="flex gap-3 mb-3">
            <div className="flex-1 py-2.5 rounded-[12px] text-[13px] leading-[18px] font-semibold text-center bg-white text-[rgba(60,60,67,0.6)]">
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-[#1A6B8A] border-t-transparent rounded-full animate-spin" />
                {activePendingUpload.status === 'uploading' ? `Uploading ${activePendingUpload.uploadProgress ?? 0}%` : 'Processing your video...'}
              </div>
            </div>
          </div>
        )}

      </div>

      {uploadModal}
    </div>
  );
}
